import { yupResolver } from '@hookform/resolvers/yup';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Stack } from '@mui/material';
import React, { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { createSearchParams, useNavigate } from 'react-router-dom';

import ExhibitionGroupAction from 'commons/Exhibition/ExhibitionGroupActions';
import Text from 'commons/Text';
import useUploadImageS3 from 'hooks/useUploadImageS3';
import GridLayout from 'layouts/GridLayout';
import useCreateExhibition from 'pages/CreateExhibition/hooks/useCreateExhibition';
import Routes from 'utils/Route';
import { groupSchema } from 'utils/Validation';

import ExhibitionGroupForm from './components/ExhibitionGroupForm';

const ExhibitionGroupStep = ({ step, setStep, informationFormState }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.account.user);

  const { uploadImage } = useUploadImageS3();

  const [groupIndex, setGroupIndex] = useState(0);

  const { control, register, watch, setValue, formState, handleSubmit } = useForm({
    defaultValues: {
      groups: [{ title: 'Group 1', description: t('description'), artworks: [] }],
    },
    mode: 'onChange',
    resolver: yupResolver(groupSchema(t)),
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'groups',
  });

  const { mutate: createExhibition } = useCreateExhibition();

  const handleAddGroup = () => {
    append({
      title: `Group ${fields?.length + 1}`,
      description: '',
      artworks: [],
    });
    setGroupIndex((prev) => prev + 1);
  };

  const handleSelectGroup = (index) => {
    setGroupIndex(index);
  };

  const handleDeleteGroup = () => {
    remove(groupIndex);
    setGroupIndex(0);
  };

  const handleSubmitForm = async (data) => {
    const informationData = informationFormState.getValues();

    const formData = {
      title: informationData?.exhibitionTitle,
      date_start: informationData?.dateStart,
      date_end: informationData?.dateEnd,
      address: informationData?.address,
      preface: informationData?.preface,
      event_type: informationData?.typeOfEvent?.key,
      organizer_name: informationData?.organizerName,
      groups: data?.groups,
      curator: informationData?.curator,
    };

    const { coverUploadFile } = informationData;
    if (
      coverUploadFile &&
      Object.keys(coverUploadFile).length > 0 &&
      (await uploadImage(coverUploadFile)).success
    ) {
      formData.cover_image = coverUploadFile.imageKey;
    }

    createExhibition(formData, {
      onSuccess: () => {
        const searchParams = createSearchParams({ tab: '5' }).toString();
        navigate({
          pathname: Routes.GalleryDetail(user?.uuid),
          search: searchParams,
        });
      },
    });
  };

  const handleBackStep = () => {
    setStep((prev) => prev - 1);
  };

  return (
    <>
      <Stack
        direction={'row'}
        sx={{ mb: 2, alignItems: 'center' }}
        onClick={handleBackStep}
      >
        <ArrowBackIcon />
        <Text
          ml={8}
          type="sm"
        >
          {t('back_step')}
        </Text>
      </Stack>
      <Text
        type="2xl"
        fontWeightBold
        variant="h1"
      >
        {t('exhibition_groupings')}
      </Text>

      <Text
        mt={10}
        mb={24}
      >
        {t('text_fill_exhibition_group')}
      </Text>

      <GridLayout spacing={2}>
        <ExhibitionGroupForm
          fields={fields}
          register={register}
          watch={watch}
          groupIndex={groupIndex}
          onDeleteGroup={handleDeleteGroup}
          setValue={setValue}
          formState={formState}
          key={groupIndex}
        />
        <ExhibitionGroupAction
          fields={fields}
          step={step}
          onAddGroup={handleAddGroup}
          onSelectGroup={handleSelectGroup}
          onNextStep={handleSubmit(handleSubmitForm)}
          watch={watch}
          user={user}
        />
      </GridLayout>
    </>
  );
};

export default ExhibitionGroupStep;
